<template>
  <div class="form-item-container mb-6 no-drag">
    <div class="fixed-content-container">
      <div class="header pb-2">
        <div class="header-title">
          <v-icon class="item-icon cursor-move" icon="mdi-drag-vertical" size="large"></v-icon>
          <div class="font-weight-black">{{ $t('i18n.textLabelTitle') }}</div>
        </div>
        <div class="header-tool-icons">
          <v-btn class="cursor-pointer" variant="text" icon @click="duplicateItem" :disabled="!userCanEditFormItems">
            <v-icon icon="$gorateDuplicate"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.duplicate') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="removeItem" :disabled="!userCanEditFormItems">
            <v-icon size="small" icon="$gorateDeleteOutline"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.remove') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="expandGrid" :disabled="!userCanEditFormItems">
            <v-icon :icon="expandGridButtonIconName" size="large"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.collapse') }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </div>
    <div ref="collapsableContentContainer" class="collapsable-content-container">
      <div style="overflow: hidden;">
        <div class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language1') }}</div>
          <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
            {{ $t('i18n.editNotAvailable') }}
          </v-tooltip>
          <v-textarea density="compact" :placeholder="$t('i18n.titleLang1')" variant="outlined" dense
            hide-details="auto" v-model="itemData.text_label_lang1" rows="4" clearable no-resize counter
            :rules="textRules" :disabled="!userCanEditFormItems"></v-textarea>
        </div>
        <div v-if="bilingual" class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language2') }}</div>
          <v-textarea density="compact" :placeholder="$t('i18n.questionLang2')" variant="outlined" dense
            hide-details="auto" v-model="itemData.text_label_lang2" rows="4" clearable no-resize counter
            :rules="textRules"></v-textarea>
        </div>
      </div>
    </div>
  </div>
  <v-divider :thickness="2" class="border-opacity-100 mb-3" color="grey"></v-divider>
</template>

<script lang="ts">
import { FormDeviceAssociation, FormItem } from '@/utils/DataTypes'
import { defineComponent, PropType, ref } from 'vue'

export default defineComponent({
  name: 'TextLabelComponent',
  setup () {
    const collapsableContentContainer = ref<HTMLElement | null>(null)
    const isExpanded = ref(true)

    const expandGrid = () => {
      if (collapsableContentContainer.value) {
        isExpanded.value = !isExpanded.value
        collapsableContentContainer.value.style.gridTemplateRows = isExpanded.value ? '1fr' : '0fr'
      }
    }

    return {
      collapsableContentContainer,
      isExpanded,
      expandGrid
    }
  },
  emits: ['duplicate-item', 'remove-item', 'form-item-update'],
  props: {
    formList: {
      type: Array as PropType<FormDeviceAssociation[]>,
      required: true,
      default: () => []
    },
    indexInTheSketchForm: {
      type: Number,
      required: true
    },
    bilingual: {
      type: Boolean,
      required: false,
      default: false
    },
    formItems: {
      type: Array as PropType<FormItem[]>,
      required: false,
      default: null
    },
    userCanEditFormItems: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  data () {
    return {
      itemData: {
        name: 'TEXT_LABEL',
        item_type_id: 1,
        text_label_lang1: '',
        text_label_lang2: '',
        required: true,
        bilingual: this.bilingual
      } as FormItem
    }
  },
  watch: {
    formItems: {
      handler () {
        if (this.formItems) {
          if (this.formItems[this.indexInTheSketchForm] !== undefined) {
            this.itemData = this.formItems[this.indexInTheSketchForm]
            delete this.itemData.question_lang1
            delete this.itemData.question_lang2
          }
        }
      },
      immediate: true
    },
    itemData: {
      handler (newItemData) {
        this.$emit('form-item-update', { formItemIndex: this.indexInTheSketchForm, data: newItemData })
      },
      deep: true
    },
    bilingual: {
      handler (newBilingual) {
        this.itemData.bilingual = newBilingual
        if (!newBilingual) {
          this.itemData.text_label_lang2 = ''
        }
      },
      immediate: true
    }
  },
  methods: {
    getFormItemFromProps (): FormItem | null {
      if (this.formItems && this.formItems[this.indexInTheSketchForm]) {
        return { ...this.formItems[this.indexInTheSketchForm] }
      }
      return null
    },
    duplicateItem () {
      this.$emit('duplicate-item', this.indexInTheSketchForm)
    },
    removeItem () {
      this.$emit('remove-item', this.indexInTheSketchForm)
    }
  },
  computed: {
    textRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.textLabelRequired')
        }, (value: string) => {
          if (value && value.length <= 300) return true
          return this.$t('i18n.textLabelLength')
        },
        (value: string) => {
          if (value && value.length >= 3) return true
          return this.$t('i18n.textLabelMinLength3')
        }
      ]
    },
    expandGridButtonIconName (): string {
      if (this.isExpanded) return 'mdi-chevron-up'
      else return 'mdi-chevron-down'
    }
  }
})
</script>

<style lang="scss" scoped>
@import '../../styles/colors.scss';

.form-item-container {
  .body {
    display: grid;
    grid-template-columns: 6.5em 1fr;
    align-items: center;
  }

  .fixed-content-container {
    .header {
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr 1fr;
      align-items: center;

      .header-title {
        grid-column: 1;
        display: grid;
        grid-template-columns: auto auto;
        justify-self: start;
        align-items: center;
      }

      .header-tool-icons {
        grid-column: 2;
        justify-self: end;
      }

    }
  }

  .collapsable-content-container {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 300ms;
  }
}
</style>
